import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';

const tabs = [
  { key: 'all', label: '全部', icon: 'ri-apps-line' },
  { key: 'training', label: '体育培训', icon: 'ri-run-line' },
  { key: 'events', label: '赛事组织', icon: 'ri-trophy-line' },
  { key: 'equipment', label: '运动装备', icon: 'ri-basketball-line' },
  { key: 'venues', label: '场馆管理', icon: 'ri-building-line' },
];

export default function Categories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState(tabParam);

  useEffect(() => {
    setActiveTab(tabParam);
  }, [tabParam]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const handleTabChange = (key: string) => {
    if (key === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ tab: key });
    }
  };

  const displayCategories = activeTab === 'all' ? categories : categories.filter((c) => c.slug === activeTab);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '服务分类' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <section className="relative w-full h-[280px] md:h-[340px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Sports%20facility%20panorama%20with%20various%20sports%20equipment%20and%20courts%2C%20warm%20lighting%2C%20modern%20interior%2C%20professional%20sports%20center%20atmosphere%2C%20warm%20orange%20and%20cream%20tones&width=1800&height=700&seq=yunkai-cat-hero&orientation=landscape"
          alt="服务分类"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/55"></div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-3">服务分类</h1>
          <p className="text-sm md:text-base text-background-50/75">按需选择最适合您的体育服务</p>
        </div>
      </section>

      <Breadcrumb items={breadcrumbItems} />

      <section className="w-full py-8 bg-background-50 border-b border-background-200/70">
        <div className="w-full px-6 md:px-8">
          <div className="flex items-center flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800'
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <main className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {displayCategories.map((category) => (
              <div
                key={category.id}
                className="bg-background-100 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/90 backdrop-blur-sm">
                      <i className={`${category.icon} text-background-50 text-lg`}></i>
                    </div>
                    <span className="text-lg font-bold font-heading text-background-50">{category.name}</span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-sm text-foreground-500 leading-relaxed mb-5">{category.description}</p>
                  <div className="space-y-2.5">
                    {category.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2.5">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                          <i className="ri-check-line text-accent-600 text-xs"></i>
                        </div>
                        <span className="text-sm text-foreground-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to={`/articles?category=${category.slug}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    查看相关资讯
                    <i className="ri-arrow-right-line text-base"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}